/**
 * CONNOX GMBH CONFIDENTIAL
 * ________________________
 *
 * Copyright (c) 2005 - 2020 Connox GmbH, All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains the property
 * of Connox GmbH and its suppliers, if any. The intellectual and
 * technical concepts contained herein are proprietary to Connox GmbH and
 * its suppliers and may be covered by German Laws and Foreign Patents,
 * patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is
 * strictly forbidden unless prior written permission is obtained from
 * Connox GmbH.
 */

import timer from "../../logger/timer";
import {getRequestConfigurationWithToken} from "../../requestHandler/getRequestConfigWithToken";
import {BaseRequestHandler} from "../../requestHandler/BaseRequestHandler";
import {IItemContent} from "../../items/interface/Item.interface";
import {ApiError} from "../../cart/requestHandlers/ApiError";
import {isError} from "../../cart/requestHandlers/CartRequestHandler";

export interface IWishlistItem {
  modified: string;
  item: IItemContent;
}

export interface IWishlistData {
  id: string;
  modified: string;
  items: IWishlistItem[];
}

export class WishlistRequestHandler {
  /**
   * GET wishlist if present
   * @param shopId
   * @param currencyCode
   * @param visitorId
   */
  @timer("API", "Customer")
  public static async getWishlist(
    shopId: number,
    currencyCode: string,
    visitorId: string,
  ): Promise<IWishlistData | null> {
    const configuration = await getRequestConfigurationWithToken();
    configuration.params = {
      currencyCode,
    };

    return BaseRequestHandler.performRequestWithPossibleError<IWishlistData | null>(
      "GET",
      `/shop/${shopId}/visitors/${visitorId}/wishlist`,
      configuration,
    ).then((response) => {
      if (isError(response)) {
        if ((response as ApiError).originalError.response?.status === 404) {
          return null;
        }

        throw response;
      }

      return response;
    });
  }

  /**
   * Add item to (potentially new) wishlist by POST
   * @param shopId
   * @param currencyCode
   * @param visitorId
   * @param itemId
   */
  @timer("API", "Customer")
  public static async postWishlistItem(
    shopId: number,
    currencyCode: string,
    visitorId: string,
    itemId: number,
  ): Promise<IWishlistData> {
    const configuration = await getRequestConfigurationWithToken<{itemId: number}>();
    configuration.data = {itemId};
    configuration.params = {currencyCode};

    return BaseRequestHandler.performRequest<IWishlistData, {itemId: number}>(
      "POST",
      `/shop/${shopId}/visitors/${visitorId}/wishlist`,
      configuration,
    );
  }

  /**
   * DELETE item from known wishlist
   * @param shopId
   * @param currencyCode
   * @param visitorId
   * @param listId
   * @param itemId
   */
  public static async deleteWishlistItem(
    shopId: number,
    currencyCode: string,
    visitorId: string,
    listId: string,
    itemId: number,
  ): Promise<IWishlistData> {
    const configuration = await getRequestConfigurationWithToken();
    configuration.params = {currencyCode};

    return BaseRequestHandler.performRequest<IWishlistData>(
      "DELETE",
      `/shop/${shopId}/visitors/${visitorId}/wishlist/${listId}/item/${itemId}`,
      configuration,
    );
  }
}
